import { useCallback, useMemo, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { initialSatisfacaoClientePesquisaListaState, useSatisfacaoClientePesquisaListaFilters } from "../hooks/SatisfacaoClientePesquisa_lista/filters";
import { useSatisfacaoClientePesquisaListaPagination } from "../hooks/SatisfacaoClientePesquisa_lista/pagination";
import { PesquisaSatiscacaoClienteLista } from "../../domain/models/pesquisa_satisfacao_cliente_lista";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useLocation } from "react-router";
import { SatisfacaoClienteDetalhe } from "../../domain/models/satisfacao_cliente_detalhe";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { PesquisaSatisfacaoClienteForm } from "../schemas/SatisfacaoClientePesquisa_lista/pesquisa_satisfacao_cliente";
import { ItemPesquisaClienteLista } from "../../domain/models/item_pesquisa_cliente_lista";
import { PesquisaSatisfacaoDropDown } from "../../domain/models/pesquisa_satisfacao_dropdown";
import { CreatePesquisaSatisfacaoClienteForm } from "../schemas/SatisfacaoClientePesquisa_lista/create_pesquisa_satisfacao_cliente";

/**
 * LoadPesquisaSatisfacaoClienteFuncState
 */

type LoadPesquisaSatisfacaoClienteFuncState = {
  idSatisfacaoCliente?: number;
  respondidaPor?: string | null;
  data?: Date | null;
  local?: string | null;
  status?: number;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useSatisfacaoClientePesquisaListaViewModel
 */

export const useSatisfacaoClientePesquisaListaViewModel = () => {

  const {
    reports: {
      getReportSatisfacaoClientePesquisaUseCase,
    },
    entregaObras: {
      getPesquisaSatisfacaoDropDownUseCase,
      getAllPesquisaSatisfacaoClienteUseCase,
      getByIdSatisfacaoClienteUseCase,
      getAllItemPesquisaClienteUseCase,
      createPesquisaSatisfacaoClienteUseCase,
      updatePesquisaSatisfacaoClienteUseCase,
      deletePesquisaSatisfacaoClienteUseCase,
    }
  } = useDependencyInjection();

  const { pushInfoNotification, pushErrorNotification, setPageSaving } = usePageLoader();

  const debouncedHandlerRef = useCreateDebounceHandler(750);

  const backOrNavigate = useBackOrNavigate();

  const location = useLocation();

  const [idSatisfacaoCliente, setIdSatisfacaoCliente] = useState<number>();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [createPesquisaSatisfacaoClienteModal, setCreatePesquisaSatisfacaoClienteModal] = useState(false);
  const [pesquisaSatifacaoClienteModal, setPesquisaSatifacaoClienteModal] = useState(false);
  const [deletePesquisaSatisfacaoClienteModal, setDeletePesquisaSatisfacaoClienteModal] = useState(false);

  const [pesquisaSatisfacaoDropdown, setPesquisaSatisfacaoDropdown] = useState<PesquisaSatisfacaoDropDown[]>();

  const [selectedPesquisaSatisfacaoCliente, setSelectedPesquisaSatisfacaoCliente] = useState<PesquisaSatiscacaoClienteLista>();
  const [satisfacaoCliente, setSatisfacaoCliente] = useState<SatisfacaoClienteDetalhe>();
  const [pesquisaSatisfacaoClientes, setPesquisaSatisfacaoClientes] = useState<PesquisaSatiscacaoClienteLista[]>();
  const [itensPesquisaCliente, setItensPesquisaCliente] = useState<ItemPesquisaClienteLista[]>();

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  const filters = useSatisfacaoClientePesquisaListaFilters();
  const pagination = useSatisfacaoClientePesquisaListaPagination();

  const resultadoMedio = useMemo(() => {
    if (pesquisaSatisfacaoClientes === undefined || pesquisaSatisfacaoClientes.length === 0) return 0;
    return pesquisaSatisfacaoClientes[0].resultadoMedio ?? 0;
  }, [
    pesquisaSatisfacaoClientes
  ]);

  /**
   * Funções de carregamento
   */

  const loadPesquisaSatisfacaoDropDown = useCallback(async () => {
    try {
      const results = await getPesquisaSatisfacaoDropDownUseCase
        .getPesquisaSatisfacaoDropDown();
      setPesquisaSatisfacaoDropdown(results);
    } catch (e: unknown) {
      setPesquisaSatisfacaoDropdown(undefined);
      throw new Error('Falha ao buscar dados para dropdowns da pesquisa de satisfação dos clientes!');
    }
  }, [
    getPesquisaSatisfacaoDropDownUseCase,
  ]);

  const loadSatisfacaoCliente = useCallback(async (id?: number) => {
    try {
      const filteredIdSatisfacaoCliente = id ?? idSatisfacaoCliente;
      const result = await getByIdSatisfacaoClienteUseCase
        .getByIdSatisfacaoCliente(filteredIdSatisfacaoCliente);
      setSatisfacaoCliente(result);
    } catch (e: unknown) {
      setSatisfacaoCliente(undefined);
      throw new Error('Falha ao buscar satisfação dos cliente!');
    }
  }, [
    idSatisfacaoCliente,
    getByIdSatisfacaoClienteUseCase,
  ]);

  const loadPesquisaSatisfacaoCliente = useCallback(async (fs?: LoadPesquisaSatisfacaoClienteFuncState) => {
    try {
      const filteredIdSatisfacaoCliente = fs?.idSatisfacaoCliente ?? idSatisfacaoCliente;
      const filteredRespondidaPor = evaluateParamValue(fs?.respondidaPor, filters.respondidaPor);
      const filteredData = evaluateParamValue(fs?.data, filters.data);
      const filteredLocal = evaluateParamValue(fs?.local, filters.local);
      const filteredStatus = evaluateParamValue(fs?.status, filters.status);

      const filteredPage = fs?.page ?? pagination.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllPesquisaSatisfacaoClienteUseCase.getAllPesquisaSatisfacaoCliente(
        filteredIdSatisfacaoCliente,
        filteredRespondidaPor,
        filteredData,
        filteredLocal,
        filteredStatus,
        filteredPage,
        filteredRowsPerPage,
      );

      setPesquisaSatisfacaoClientes(getAllPesquisaSatisfacaoClienteUseCase.items);
      pagination.setTotalItems(getAllPesquisaSatisfacaoClienteUseCase.totalItems);
    } catch (e: unknown) {
      setPesquisaSatisfacaoClientes(undefined);
      throw new Error('Falha ao buscar pesquisa de satisfação dos clientes!');
    }
  }, [
    idSatisfacaoCliente,
    filters,
    pagination,
    getAllPesquisaSatisfacaoClienteUseCase,
  ]);

  const loadItensPesquisaCliente = useCallback(async (idPesquisaSatisfacaoCliente: number) => {
    try {
      const result = await getAllItemPesquisaClienteUseCase
        .getAllItemPesquisaCliente(idPesquisaSatisfacaoCliente);
      setItensPesquisaCliente(result);
    } catch (e: unknown) {
      setItensPesquisaCliente(undefined);
      throw new Error('Falha ao buscar itens da pesquisa de satisfação dos clientes!');
    }
  }, [
    getAllItemPesquisaClienteUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationId = params.get('id');
      const idStr = locationId ?? null;
      const id = idStr ? parseInt(idStr, 10) : undefined;

      setIdSatisfacaoCliente(id);

      await loadPesquisaSatisfacaoDropDown();

      await loadSatisfacaoCliente(id);

      useSatisfacaoClientePesquisaListaPagination.setState({ page: 1 });
      await loadPesquisaSatisfacaoCliente({ idSatisfacaoCliente: id, page: 1 });

    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados da pagina!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: '/PesquisaCliente_lista' });
  }, [
    backOrNavigate
  ]);

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);

      await loadPesquisaSatisfacaoDropDown();

      await loadSatisfacaoCliente();

      await loadPesquisaSatisfacaoCliente();

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao recarregar dados da página!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadPesquisaSatisfacaoDropDown,
    loadSatisfacaoCliente,
    loadPesquisaSatisfacaoCliente,
    pushErrorNotification,
  ]);

  const handleCreatePesquisaSatisfacaoClienteOpenState = useCallback((openState: boolean) => {
    setCreatePesquisaSatisfacaoClienteModal(openState);
  }, [
  ]);

  const handleCreatePesquisaSatisfacaoClienteSubmit = useCallback(async (form: CreatePesquisaSatisfacaoClienteForm) => {
    if (idSatisfacaoCliente) {
      try {
        setSaving(true);
        setPageSaving(true);

        await createPesquisaSatisfacaoClienteUseCase.createPesquisaSatisfacaoCliente(
          idSatisfacaoCliente,
          form.pesquisaSatisfacao,
        );

        await loadPesquisaSatisfacaoCliente();

        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');

        setCreatePesquisaSatisfacaoClienteModal(false);
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, "Falha ao criar pesquisa de satisfação do cliente!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idSatisfacaoCliente,
    createPesquisaSatisfacaoClienteUseCase,
    loadPesquisaSatisfacaoCliente,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handlePesquisaSatisfacaoClienteOpenState = useCallback((idPesquisaSatisfacaoCliente: number | null, openState: boolean) => {
    if (openState) {
      if (idPesquisaSatisfacaoCliente && pesquisaSatisfacaoClientes) {
        setSelectedPesquisaSatisfacaoCliente(pesquisaSatisfacaoClientes.find(p => p.idPesquisaSatisfacaoCliente === idPesquisaSatisfacaoCliente));
      } else {
        setSelectedPesquisaSatisfacaoCliente(undefined);
      }
    }
    setPesquisaSatifacaoClienteModal(openState);
  }, [
    pesquisaSatisfacaoClientes,
  ]);

  const handlePesquisaSatisfacaoClienteSubmit = useCallback(async (form: PesquisaSatisfacaoClienteForm) => {
    if (selectedPesquisaSatisfacaoCliente) {
      try {
        setSaving(true);
        setPageSaving(true);

        await updatePesquisaSatisfacaoClienteUseCase.updatePesquisaSatisfacaoCliente(
          selectedPesquisaSatisfacaoCliente.idPesquisaSatisfacaoCliente,
          form.respondidaPor,
          form.data,
          form.local,
          form.status,
          form.observacao,
          form.itens,
        );

        await loadPesquisaSatisfacaoCliente();

        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');

        setPesquisaSatifacaoClienteModal(false);
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, "Falha ao criar pesquisa de satisfação do cliente!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    selectedPesquisaSatisfacaoCliente,
    updatePesquisaSatisfacaoClienteUseCase,
    loadPesquisaSatisfacaoCliente,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleDeletePesquisaSatisfacaoClienteSubmit = useCallback(async () => {
    if (selectedPesquisaSatisfacaoCliente) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deletePesquisaSatisfacaoClienteUseCase.deletePesquisaSatisfacaoCliente(
          selectedPesquisaSatisfacaoCliente.idPesquisaSatisfacaoCliente,
        );

        await loadPesquisaSatisfacaoCliente();

        pushInfoNotification('Sucesso', 'Registro excluído com sucesso!');

        setDeletePesquisaSatisfacaoClienteModal(false);
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, "Falha ao criar pesquisa de satisfação do cliente!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    selectedPesquisaSatisfacaoCliente,
    deletePesquisaSatisfacaoClienteUseCase,
    loadPesquisaSatisfacaoCliente,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handlePageChange = useCallback((page: number) => {
    try {
      setLoading(true);
      useSatisfacaoClientePesquisaListaPagination.setState({ page: page + 1 });
      loadPesquisaSatisfacaoCliente({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadPesquisaSatisfacaoCliente,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback((rowsPerPage: number) => {
    try {
      setLoading(true);
      useSatisfacaoClientePesquisaListaPagination.setState({ rowsPerPage: rowsPerPage, page: 1 });
      loadPesquisaSatisfacaoCliente({ rowsPerPage: rowsPerPage, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadPesquisaSatisfacaoCliente,
    pushErrorNotification,
  ]);

  const handleRespondidaPorChange = useDebouncedCallback(debouncedHandlerRef, (value: string | null) => {
    useSatisfacaoClientePesquisaListaFilters.setState({ respondidaPor: value });
    return async () => {
      try {
        setLoading(true);
        useSatisfacaoClientePesquisaListaPagination.setState({ page: 1 });
        await loadPesquisaSatisfacaoCliente({ respondidaPor: value, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, "Falha ao buscar dados da pagina!"));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadPesquisaSatisfacaoCliente,
    pushErrorNotification,
  ]);

  const handleDataChange = useCallback(async (value: Date | null) => {
    try {
      setLoading(true);
      useSatisfacaoClientePesquisaListaFilters.setState({ data: value });
      useSatisfacaoClientePesquisaListaPagination.setState({ page: 1 });
      await loadPesquisaSatisfacaoCliente({ data: value, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadPesquisaSatisfacaoCliente,
    pushErrorNotification,
  ]);

  const handleLocalChange = useDebouncedCallback(debouncedHandlerRef, (value: string | null) => {
    useSatisfacaoClientePesquisaListaFilters.setState({ local: value });
    return async () => {
      try {
        setLoading(true);
        useSatisfacaoClientePesquisaListaPagination.setState({ page: 1 });
        await loadPesquisaSatisfacaoCliente({ local: value, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, "Falha ao buscar dados da pagina!"));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadPesquisaSatisfacaoCliente,
    pushErrorNotification,
  ]);

  const handleStatusChange = useCallback(async (value: number) => {
    try {
      setLoading(true);
      useSatisfacaoClientePesquisaListaFilters.setState({ status: value });
      useSatisfacaoClientePesquisaListaPagination.setState({ page: 1 });
      await loadPesquisaSatisfacaoCliente({ status: value, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadPesquisaSatisfacaoCliente,
    pushErrorNotification,
  ]);

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      useSatisfacaoClientePesquisaListaFilters.setState({ ...initialSatisfacaoClientePesquisaListaState });
      useSatisfacaoClientePesquisaListaPagination.setState({ page: 1 });
      await loadPesquisaSatisfacaoCliente({ ...initialSatisfacaoClientePesquisaListaState, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadPesquisaSatisfacaoCliente,
    pushErrorNotification,
  ]);

  const handleOpenEditarSatisfacaoClientePesquisa = useCallback(async (idPesquisaSatisfacaoCliente: number | null, openState: boolean) => {
    if (openState) {
      if (idPesquisaSatisfacaoCliente && pesquisaSatisfacaoClientes) {
        const selected = pesquisaSatisfacaoClientes.find(p => p.idPesquisaSatisfacaoCliente === idPesquisaSatisfacaoCliente);
        if (selected) {
          try {
            setLoading(true);
            setSelectedPesquisaSatisfacaoCliente(selected);
            await loadItensPesquisaCliente(selected.idPesquisaSatisfacaoCliente)
          } catch (e: unknown) {
            pushErrorNotification('Erro', processError(e, "Falha ao buscar itens da pesquisa de satisfação!"));
          } finally {
            setLoading(false);
          }
        }
      } else {
        setSelectedPesquisaSatisfacaoCliente(undefined);
      }
    }
    setPesquisaSatifacaoClienteModal(openState);
  }, [
    pesquisaSatisfacaoClientes,
    loadItensPesquisaCliente,
    pushErrorNotification,
  ]);

  const handleOpenDeleteSatisfacaoClientePesquisa = useCallback((idPesquisaSatisfacaoCliente: number | null, openState: boolean) => {
    if (openState) {
      if (idPesquisaSatisfacaoCliente && pesquisaSatisfacaoClientes) {
        setSelectedPesquisaSatisfacaoCliente(pesquisaSatisfacaoClientes.find(p => p.idPesquisaSatisfacaoCliente === idPesquisaSatisfacaoCliente));
      } else {
        setSelectedPesquisaSatisfacaoCliente(undefined);
      }
    }
    setDeletePesquisaSatisfacaoClienteModal(openState);
  }, [
    pesquisaSatisfacaoClientes,
  ]);

  const handleExportarPesquisaSatisfacaoCliente = useCallback(async (idPesquisaSatisfacaoCliente: number) => {
    if (idSatisfacaoCliente) {
      try {
        setLoading(true);

        await startReportViewerProcess({
          fetchCallback: async (requiredMimeType) =>
            await getReportSatisfacaoClientePesquisaUseCase.getReportSatisfacaoClientePesquisa(
              requiredMimeType,
              idPesquisaSatisfacaoCliente,
            ),
        });

      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, "Falha ao exportar dados!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    idSatisfacaoCliente,
    getReportSatisfacaoClientePesquisaUseCase,
    startReportViewerProcess,
    pushErrorNotification,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    createPesquisaSatisfacaoClienteModal,
    pesquisaSatifacaoClienteModal,
    deletePesquisaSatisfacaoClienteModal,
    pesquisaSatisfacaoDropdown: pesquisaSatisfacaoDropdown ?? [],
    selectedPesquisaSatisfacaoCliente: selectedPesquisaSatisfacaoCliente ?? null,
    satisfacaoCliente: satisfacaoCliente ?? null,
    pesquisaSatisfacaoClientes: pesquisaSatisfacaoClientes ?? [],
    itensPesquisaCliente: itensPesquisaCliente ?? [],
    reportViewerState,
    filters,
    pagination,
    resultadoMedio,
    handleBack,
    handleReload,
    handleCreatePesquisaSatisfacaoClienteOpenState,
    handleCreatePesquisaSatisfacaoClienteSubmit,
    handlePesquisaSatisfacaoClienteOpenState,
    handlePesquisaSatisfacaoClienteSubmit,
    handleDeletePesquisaSatisfacaoClienteSubmit,
    handlePageChange,
    handleRowsPerPageChange,
    handleRespondidaPorChange,
    handleDataChange,
    handleLocalChange,
    handleStatusChange,
    handleClearFilters,
    handleOpenEditarSatisfacaoClientePesquisa,
    handleOpenDeleteSatisfacaoClientePesquisa,
    handleExportarPesquisaSatisfacaoCliente,
  };
}